/**
 * Lens requests use the same askRichard access token as the rest of the
 * product. Keep the reader-facing state precise: a missing session is
 * recoverable by signing in, whereas a 403 and an unavailable service are not
 * the same problem.
 */
export function lensReadFailureText(error: unknown): string {
  const requestError = error as { name?: string; status?: number; message?: string } | null;
  const status = requestError?.status;

  if (requestError?.name === 'AuthorizationError' || status === 401) {
    return 'Sign in to askRichard to access Lenses, then retry.';
  }
  if (status === 403) {
    return 'This account cannot read Lenses.';
  }
  return 'Lenses are unavailable right now.';
}

export type SharedLensFailure = {
  heading: string;
  detail: string;
  canRetry: boolean;
  canRequestAccess?: boolean;
};

/** True for the version 1–5 UUID shape used by reporting Lens ids. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isLensIdShaped(lensId: string | null | undefined): boolean {
  return typeof lensId === 'string' && UUID_PATTERN.test(lensId.trim());
}

function statusOf(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Turn a failed shared dashboard request into a reader-facing state. This is
 * deliberately separate from the landing/orientation experience so shared
 * links remain reliable without changing the cold-open flow.
 */
export function describeSharedLensFailure(lensId: string | null, error: unknown): SharedLensFailure {
  if (!isLensIdShaped(lensId)) {
    return {
      heading: 'This link does not name a Lens',
      detail: 'The address was changed or cut short on the way here. Ask whoever sent it to send the link again.',
      canRetry: false,
    };
  }

  const status = statusOf(error);
  if (status === 401 || (error as { name?: string } | null)?.name === 'AuthorizationError') {
    return {
      heading: 'Sign in to open this Lens',
      detail: 'Your session has ended. Sign in to askRichard and open the link again.',
      canRetry: true,
    };
  }
  if (status === 403) {
    return {
      heading: 'You do not have access to this Lens',
      detail: '',
      canRetry: false,
      canRequestAccess: true,
    };
  }
  if (status === 404) {
    return {
      heading: 'Lens not found',
      detail: 'The link may be incorrect or the Lens may have been removed. Browse your Lenses to find it.',
      canRetry: false,
    };
  }
  return {
    heading: 'This Lens could not be opened',
    detail: '',
    canRetry: true,
  };
}
